/**
 * Stream B — B36: Social Listening Dashboard
 *
 * Mention feed, sentiment gauge, competitor comparison, keyword alerts.
 * Real apiFetch. No mocks. CSS variables only.
 */
import { useState, useEffect, useCallback } from "react";
import { apiFetch } from "@/lib/api-client";
import { formatDate, formatNumber } from "@/lib/i18n";
import {
  Globe,
  TrendingUp,
  TrendingDown,
  Minus,
  Bell,
  BellOff,
  ExternalLink,
  Users,
  MessageCircle,
  AlertCircle,
  Search,
} from "lucide-react";

interface SocialMention {
  id: string;
  platform: string;
  authorName: string;
  authorHandle: string;
  content: string;
  sentiment: "positive" | "neutral" | "negative";
  reach: number;
  engagements: number;
  url: string;
  createdAt: string;
}

interface SocialListeningData {
  mentions: SocialMention[];
  sentimentBreakdown: { positive: number; neutral: number; negative: number };
  totalMentions: number;
  mentionTrend: "up" | "down" | "stable";
  competitorComparison: Array<{ name: string; mentions: number; sentiment: number }>;
  alerts: Array<{ id: string; keyword: string; enabled: boolean; matchCount: number }>;
}

interface MentionAlertContext {
  mention: SocialMention;
  type: "opportunity" | "urgent_issue";
  label: string;
  operatorAction: string;
}

const OPPORTUNITY_PATTERN = /\b(pricing|price|book|booking|demo|buy|interested|looking for|quote|this week)\b/i;
const URGENT_ISSUE_PATTERN = /\b(urgent|outage|broken|awful|angry|refund|cancel|down|complaint|issue)\b/i;

function getMentionAlertContext(mention: SocialMention): MentionAlertContext | null {
  if (mention.sentiment === "negative" && URGENT_ISSUE_PATTERN.test(mention.content)) {
    return {
      mention,
      type: "urgent_issue",
      label: "Urgent issue",
      operatorAction: "Escalate with context",
    };
  }

  if (mention.sentiment === "positive" && OPPORTUNITY_PATTERN.test(mention.content)) {
    return {
      mention,
      type: "opportunity",
      label: "Opportunity",
      operatorAction: "Route to sales",
    };
  }

  return null;
}

function MentionCard({ mention }: { mention: SocialMention }) {
  const sentimentBorder =
    mention.sentiment === "positive" ? "border-l-[var(--color-success)]"
    : mention.sentiment === "negative" ? "border-l-[var(--color-error)]"
    : "border-l-[var(--color-warning)]";

  return (
    <div className={`rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] p-4 border-l-4 ${sentimentBorder}`}>
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-2">
          <Globe className="h-4 w-4 text-[var(--color-primary)]" />
          <span className="text-sm font-medium text-[var(--color-foreground)]">{mention.authorName}</span>
          <span className="text-xs text-[var(--color-muted-foreground)]">@{mention.authorHandle}</span>
          <span className="text-xs text-[var(--color-muted-foreground)]">on {mention.platform}</span>
        </div>
        {mention.url.startsWith("https://") && (
          <a
            href={mention.url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)] transition-colors"
          >
            <ExternalLink className="h-4 w-4" />
          </a>
        )}
      </div>

      <p className="mt-2 text-sm text-[var(--color-foreground)]">{mention.content}</p>

      <div className="mt-3 flex items-center gap-4 text-xs text-[var(--color-muted-foreground)]">
        <span className="flex items-center gap-1">
          <Users className="h-3 w-3" />
          {formatNumber(mention.reach)} reach
        </span>
        <span className="flex items-center gap-1">
          <MessageCircle className="h-3 w-3" />
          {mention.engagements} engagements
        </span>
        <span>{formatDate(mention.createdAt)}</span>
      </div>
    </div>
  );
}

function OperatorAlertRail({ alerts }: { alerts: MentionAlertContext[] }) {
  if (alerts.length === 0) return null;

  return (
    <section
      aria-label="Operator listening alerts"
      className="rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] p-4"
    >
      <div className="mb-3 flex items-center justify-between gap-3">
        <h2 className="text-sm font-semibold text-[var(--color-foreground)]">Operator Alerts</h2>
        <span className="text-xs text-[var(--color-muted-foreground)]">{alerts.length} surfaced</span>
      </div>
      <div className="grid gap-3 md:grid-cols-2">
        {alerts.map((alert) => (
          <article
            key={`${alert.type}-${alert.mention.id}`}
            className={`rounded-lg border p-3 ${
              alert.type === "urgent_issue"
                ? "border-[var(--color-error)]/30 bg-[var(--color-error)]/5"
                : "border-[var(--color-success)]/30 bg-[var(--color-success)]/5"
            }`}
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-sm font-semibold text-[var(--color-foreground)]">{alert.label}</p>
                <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">
                  {alert.mention.platform} · {alert.mention.authorName} · {alert.mention.sentiment}
                </p>
              </div>
              <span className="rounded-full bg-[var(--color-background)] px-2 py-1 text-xs font-medium text-[var(--color-foreground)]">
                {alert.operatorAction}
              </span>
            </div>
            <p className="mt-2 text-sm text-[var(--color-foreground)]">{alert.mention.content}</p>
          </article>
        ))}
      </div>
    </section>
  );
}

function SentimentGauge({ breakdown }: { breakdown: { positive: number; neutral: number; negative: number } }) {
  const total = breakdown.positive + breakdown.neutral + breakdown.negative;
  if (total === 0) return null;

  const posPct = Math.round((breakdown.positive / total) * 100);
  const neuPct = Math.round((breakdown.neutral / total) * 100);
  const negPct = 100 - posPct - neuPct;

  return (
    <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] p-4">
      <h3 className="text-sm font-medium text-[var(--color-muted-foreground)] mb-3">Sentiment Breakdown</h3>
      <div className="flex h-4 rounded-full overflow-hidden mb-3">
        <div className="bg-[var(--color-success)] transition-all" style={{ width: `${posPct}%` }} />
        <div className="bg-[var(--color-warning)] transition-all" style={{ width: `${neuPct}%` }} />
        <div className="bg-[var(--color-error)] transition-all" style={{ width: `${negPct}%` }} />
      </div>
      <div className="flex justify-between text-xs">
        <span className="text-[var(--color-success)]">{posPct}% Positive</span>
        <span className="text-[var(--color-warning)]">{neuPct}% Neutral</span>
        <span className="text-[var(--color-error)]">{negPct}% Negative</span>
      </div>
    </div>
  );
}

export default function SocialListening() {
  const [data, setData] = useState<SocialListeningData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);
  const [filter, setFilter] = useState<"all" | "positive" | "neutral" | "negative">("all");

  const fetchData = useCallback(async () => {
    try {
      const res = await apiFetch<{ data: SocialListeningData }>("/crm/social/listening");
      setData(res.data);
    } catch {
      setError(true);
      setData(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="h-8 w-64 animate-pulse rounded bg-[var(--color-surface)]" />
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          {[1, 2, 3].map((i) => <div key={i} className="h-28 animate-pulse rounded-xl bg-[var(--color-surface)]" />)}
        </div>
        <div className="h-96 animate-pulse rounded-xl bg-[var(--color-surface)]" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center gap-2 rounded-xl border border-[var(--color-error)]/20 bg-[var(--color-error)]/5 p-4 text-sm text-[var(--color-error)]">
        <AlertCircle className="h-4 w-4" />
        Failed to load social listening data.
      </div>
    );
  }

  if (!data || data.totalMentions === 0) {
    return (
      <div className="rounded-xl border border-dashed border-[var(--color-border)] bg-[var(--color-surface)] p-12 text-center">
        <Globe className="mx-auto h-10 w-10 text-[var(--color-muted-foreground)] mb-3" />
        <p className="font-medium text-[var(--color-foreground)]">No social mentions yet</p>
        <p className="text-sm text-[var(--color-muted-foreground)] mt-1">
          Connect your social accounts and set up keyword tracking to monitor brand mentions across platforms.
        </p>
      </div>
    );
  }

  const TrendIcon = data.mentionTrend === "up" ? TrendingUp
    : data.mentionTrend === "down" ? TrendingDown
    : Minus;
  const trendColor = data.mentionTrend === "up" ? "text-[var(--color-success)]"
    : data.mentionTrend === "down" ? "text-[var(--color-error)]"
    : "text-[var(--color-muted-foreground)]";

  const filteredMentions = filter === "all"
    ? data.mentions
    : data.mentions.filter((m) => m.sentiment === filter);
  const operatorAlerts = data.mentions
    .map(getMentionAlertContext)
    .filter((alert): alert is MentionAlertContext => alert !== null);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-[var(--color-foreground)]">Social Listening</h1>
        <div className={`flex items-center gap-2 text-sm ${trendColor}`}>
          <TrendIcon className="h-4 w-4" />
          {data.totalMentions} mentions
        </div>
      </div>

      {/* Sentiment + Competitor + Alerts */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <SentimentGauge breakdown={data.sentimentBreakdown} />

        <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] p-4">
          <h3 className="text-sm font-medium text-[var(--color-muted-foreground)] mb-3">Competitor Comparison</h3>
          {data.competitorComparison.length === 0 ? (
            <p className="text-sm text-[var(--color-muted-foreground)]">
              Social competitor comparison is not wired yet. Use competitor monitoring for live review benchmarks.
            </p>
          ) : (
            <div className="space-y-2">
              {data.competitorComparison.map((comp) => (
                <div key={comp.name} className="flex items-center justify-between">
                  <span className="text-sm text-[var(--color-foreground)]">{comp.name}</span>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-[var(--color-muted-foreground)]">{comp.mentions} mentions</span>
                    <span className={`text-xs font-medium ${
                      comp.sentiment >= 60 ? "text-[var(--color-success)]"
                      : comp.sentiment >= 40 ? "text-[var(--color-warning)]"
                      : "text-[var(--color-error)]"
                    }`}>
                      {comp.sentiment}% positive
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] p-4">
          <h3 className="text-sm font-medium text-[var(--color-muted-foreground)] mb-3">Keyword Alerts</h3>
          {data.alerts.length === 0 ? (
            <p className="text-sm text-[var(--color-muted-foreground)]">
              No tracked keywords configured yet.
            </p>
          ) : (
            <div className="space-y-2">
              {data.alerts.map((alert) => (
                <div key={alert.id} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {alert.enabled ? (
                      <Bell className="h-3.5 w-3.5 text-[var(--color-primary)]" />
                    ) : (
                      <BellOff className="h-3.5 w-3.5 text-[var(--color-muted-foreground)]" />
                    )}
                    <span className="text-sm text-[var(--color-foreground)]">{alert.keyword}</span>
                  </div>
                  <span className="text-xs text-[var(--color-muted-foreground)]">{alert.matchCount} matches</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <OperatorAlertRail alerts={operatorAlerts} />

      {/* Filter + Mention Feed */}
      <div>
        <div className="flex items-center gap-2 mb-4">
          <Search className="h-4 w-4 text-[var(--color-muted-foreground)]" />
          <span className="text-sm font-medium text-[var(--color-foreground)]">Mention Feed</span>
          <div className="ml-auto flex gap-1">
            {(["all", "positive", "neutral", "negative"] as const).map((f) => (
              <button
                type="button"
                key={f}
                onClick={() => setFilter(f)}
                className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                  filter === f
                    ? "bg-[var(--color-primary)] text-white"
                    : "bg-[var(--color-surface)] text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)]"
                }`}
              >
                {f.charAt(0).toUpperCase() + f.slice(1)}
              </button>
            ))}
          </div>
        </div>

        <div aria-label="Mention feed results" className="space-y-3">
          {filteredMentions.length === 0 ? (
            <p className="text-center text-sm text-[var(--color-muted-foreground)] py-8">
              No {filter} mentions found.
            </p>
          ) : (
            filteredMentions.map((mention) => (
              <MentionCard key={mention.id} mention={mention} />
            ))
          )}
        </div>
      </div>
    </div>
  );
}
